import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { mapDto } from '@shared/operators';
import {
    LearningPortfolioAttachment,
    LearningPortfolioAttachmentDto,
    LearningPortfolioCategory,
    LearningPortfolioCategoryDto,
    LearningPortfolioParams,
    LearningPortfolioParamsDto,
    LearningPortfolioNote,
    LearningPortfolioNoteDto,
    LearningPortfolioNoteList,
    LearningPortfolioNoteListDto,
    LearningPortfolioNoteUpdate,
    LearningPortfolioNoteUpdateDto,
    LearningPortfolioNoteValue,
    LearningPortfolioNoteValueDto,
} from '../models';

@Injectable({ providedIn: 'root' })
export class HttpService {
    private httpClient = inject(HttpClient);

    getNotes(value: LearningPortfolioParams): Observable<LearningPortfolioNoteList> {
        const { params } = new LearningPortfolioParamsDto(value);

        return this.httpClient
            .get<LearningPortfolioNoteListDto>('learning-portfolio/notes', { params })
            .pipe(mapDto((response) => new LearningPortfolioNoteList(response)));
    }

    getNote(id: string): Observable<LearningPortfolioNote> {
        return this.httpClient.get<LearningPortfolioNoteDto>(`learning-portfolio/notes/${id}`).pipe(mapDto((dto) => new LearningPortfolioNote(dto)));
    }

    getCategories(): Observable<LearningPortfolioCategory[]> {
        return this.httpClient
            .get<LearningPortfolioCategoryDto[]>('learning-portfolio/categories')
            .pipe(mapDto((response) => response.map((dto) => new LearningPortfolioCategory(dto))));
    }

    createCategory(name: string): Observable<LearningPortfolioCategory> {
        return this.httpClient
            .post<LearningPortfolioCategoryDto>('learning-portfolio/categories', { name })
            .pipe(mapDto((dto) => new LearningPortfolioCategory(dto)));
    }

    uploadAttachments(files: File[]): Observable<LearningPortfolioAttachment[]> {
        const body = new FormData();
        files.forEach((file) => body.append('files[]', file));

        return this.httpClient
            .post<LearningPortfolioAttachmentDto[]>('learning-portfolio/notes/attachments', body)
            .pipe(mapDto((response) => response.map((dto) => new LearningPortfolioAttachment(dto))));
    }

    createNote(value: LearningPortfolioNoteValue): Observable<LearningPortfolioNote> {
        return this.httpClient
            .post<LearningPortfolioNoteDto>('learning-portfolio/notes', new LearningPortfolioNoteValueDto(value))
            .pipe(mapDto((dto) => new LearningPortfolioNote(dto)));
    }

    updateNote(id: string, value: LearningPortfolioNoteUpdate): Observable<LearningPortfolioNote> {
        return this.httpClient
            .patch<LearningPortfolioNoteDto>(`learning-portfolio/notes/${id}`, new LearningPortfolioNoteUpdateDto(value))
            .pipe(mapDto((dto) => new LearningPortfolioNote(dto)));
    }

    deleteNote(id: string): Observable<void> {
        return this.httpClient.delete<void>(`learning-portfolio/notes/${id}`);
    }
}
